import React from "react";
import SidebarShell from "../common/SidebarShell";
import { GAME_ICON } from "../../constants/icons";

/**
 * PlayListSidebar - Sidebar showing the user's Play List.
 *
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - playList: Array<{ game_id, name, thumbnail, year_published }>
 * - loading: boolean
 * - onRemove: (gameId: number) => void
 * - onClear: () => void
 */
function PlayListSidebar({ isOpen, onClose, playList, loading, onRemove, onClear, onLaunchJetSetGo }) {
  return (
    <SidebarShell
      isOpen={isOpen}
      onClose={onClose}
      title="Play List"
      width="380px"
      contentPadding="1rem"
      dataTestId="play-list-sidebar"
    >
      <div className="play-list-sidebar" style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Header actions */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1rem",
          }}
        >
          <span style={{ color: "var(--text-secondary, #666)", fontSize: "0.9rem" }}>
            {playList.length} {playList.length === 1 ? "game" : "games"}
          </span>
          {playList.length > 0 && (
            <button
              type="button"
              onClick={onClear}
              className="clear-list-button"
              style={{
                padding: "0.35rem 0.75rem",
                border: "1px solid var(--border-color, #ddd)",
                borderRadius: "4px",
                backgroundColor: "transparent",
                color: "var(--text-secondary, #666)",
                cursor: "pointer",
                fontSize: "0.85rem",
              }}
            >
              Clear list
            </button>
          )}
        </div>

        {/* Loading state */}
        {loading && playList.length === 0 && (
          <div style={{ textAlign: "center", padding: "2rem", color: "var(--text-secondary, #666)" }}>
            Loading Play List...
          </div>
        )}

        {/* Empty state */}
        {!loading && playList.length === 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "2rem",
              textAlign: "center",
              color: "var(--text-secondary, #666)",
              gap: "0.75rem",
              flex: 1,
            }}
          >
            <div style={{ fontSize: "2rem", opacity: 0.5 }}>{GAME_ICON}</div>
            <p style={{ margin: 0, fontSize: "1rem", color: "var(--text-primary, #333)" }}>
              Your Play List is empty
            </p>
            <p style={{ margin: 0, fontSize: "0.85rem" }}>
              Add games from tiles or Jet Set Go results.
            </p>
          </div>
        )}

        {/* Launch CTA */}
        {onLaunchJetSetGo && (
          <button
            type="button"
            onClick={onLaunchJetSetGo}
            className="play-list-launch-cta"
            style={{
              width: "100%",
              padding: "0.75rem 1rem",
              marginBottom: "1rem",
              border: "none",
              borderRadius: "8px",
              background: "var(--pista-green-primary, #2E7D32)",
              color: "var(--pista-button-text, white)",
              fontSize: "0.95rem",
              fontWeight: "600",
              cursor: "pointer",
              transition: "background-color 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--pista-green-hover, #388E3C)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "var(--pista-green-primary, #2E7D32)";
            }}
          >
            Launch in Jet Set Go
          </button>
        )}

        {/* List */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.75rem",
            overflowY: "auto",
            flex: 1,
          }}
        >
          {playList.map((game) => {
            const gameId = game.game_id || game.id;
            return (
              <div
                key={gameId}
                className="play-list-item"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  padding: "0.5rem",
                  borderRadius: "8px",
                  backgroundColor: "var(--bg-secondary, #f8f9fa)",
                  border: "1px solid var(--border-color, #e0e0e0)",
                }}
              >
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "4px",
                    overflow: "hidden",
                    flexShrink: 0,
                    backgroundColor: "var(--bg-tertiary, #e0e0e0)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {game.thumbnail ? (
                    <img
                      src={game.thumbnail}
                      alt={game.name}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <span style={{ fontSize: "1.2rem" }}>{GAME_ICON}</span>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "0.95rem",
                      fontWeight: 500,
                      color: "var(--text-primary, #333)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={game.name}
                  >
                    {game.name}
                  </p>
                  {game.year_published && (
                    <p style={{ margin: "0.15rem 0 0", fontSize: "0.8rem", color: "var(--text-secondary, #666)" }}>
                      {game.year_published}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(gameId)}
                  title="Remove"
                  aria-label={`Remove ${game.name} from Play List`}
                  className="play-list-remove-button"
                  style={{
                    width: "28px",
                    height: "28px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "none",
                    borderRadius: "50%",
                    backgroundColor: "transparent",
                    color: "var(--text-secondary, #666)",
                    cursor: "pointer",
                    fontSize: "1.1rem",
                    transition: "background-color 0.2s ease, color 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "var(--error-color, #f44336)";
                    e.currentTarget.style.color = "white";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "var(--text-secondary, #666)";
                  }}
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </SidebarShell>
  );
}

export default PlayListSidebar;
